import { ActivatedRoute } from '@angular/router';
import { ApiService } from './../services/api.service';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { UserService } from '../services/user.service';
import { Subscription } from 'rxjs';

interface BaseNotification {
  id: string;
  type: string;
  timeAgo: string;
  created_at: Date;
  data: {
    username: string;
    type: string;
  };
}

interface BountyCommentNotification extends BaseNotification {
  type: 'bounty_comment';
  data: BaseNotification['data'] & {
    bounty_id: number;
    message: string;
  };
}

interface ClaimCommentNotification extends BaseNotification {
  type: 'claim_comment';
  data: BaseNotification['data'] & {
    bounty_id: number;
    claim_id: number;
    message: string;
  };
}

interface ClaimMadeNotification extends BaseNotification {
  type: 'claim_made';
  data: BaseNotification['data'] & {
    claim_id: number;
  };
}

interface ClaimResultNotification extends BaseNotification {
  type: 'claim_accepted' | 'claim_denied' | 'claim_withdrawn' | 'refund';
  data: BaseNotification['data'] & {
    claim_id: number;
  };
}

interface BountyCompletedNotification extends BaseNotification {
  type: 'bounty_completed';
  data: BaseNotification['data'] & {
    bounty_id: number;
    username?: string;
  };
}

type Notification =
  | BountyCommentNotification
  | ClaimCommentNotification
  | ClaimMadeNotification
  | ClaimResultNotification
  | BountyCompletedNotification;

interface NotificationListItem {
  header: string;
  icon: string;
  timeAgo: string;
  link: string;
}

@Component({
  selector: 'app-notifications',
  templateUrl: './notifications.component.html',
  styleUrls: ['./notifications.component.scss'],
})
export class NotificationsComponent implements OnInit, OnDestroy {
  public user: any;
  private unreadNotifications: Notification[];
  public unreadNotificationsList: NotificationListItem[] = [];
  public readNotificationsList: NotificationListItem[] = [];
  private subscriptions: Subscription[] = [];
  public newNotificationsCount = 0;

  constructor(
    private userService: UserService,
    private apiService: ApiService,
    route: ActivatedRoute,
  ) {
    this.subscriptions.push(
      route.data.subscribe(({ data }) => {
        this.mapDataToState(data.user);
      }),
    );
  }

  ngOnInit() {
    // Listen for new messages
    this.subscriptions.push(
      this.userService.user.subscribe(user => {
        // Check how many new unreads there are
        this.newNotificationsCount = user.unread_notifications.filter(
          noti =>
            !this.unreadNotifications.some(notiOld => notiOld.id === noti.id),
        ).length;
      }),
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  private async markAsRead() {
    const lastUnread = this.unreadNotifications[0];
    if (!lastUnread) {
      return;
    }

    await this.apiService
      .post('mark-notifications-read', {
        date: lastUnread.created_at,
      })
      .toPromise();
    await this.userService.refreshUser();
  }

  private mapDataToState(user) {
    this.user = user;
    this.unreadNotifications = user.unread_notifications;
    this.unreadNotificationsList = user.unread_notifications
      .map(this.mapNotification.bind(this))
      .filter(x => !!x);
    this.readNotificationsList = user.read_notifications
      .map(this.mapNotification.bind(this))
      .filter(x => !!x);

    this.newNotificationsCount = 0;
    this.markAsRead();
  }

  private mapNotification(notification: Notification): NotificationListItem {
    switch (notification.data.type) {
      case 'bounty_comment':
        return this.bountyCommentData(<BountyCommentNotification>notification);
      case 'claim_comment':
        return this.claimCommentData(<ClaimCommentNotification>notification);
      case 'claim_made':
        return this.claimMadeData(<ClaimMadeNotification>notification);
      case 'claim_accepted':
      case 'claim_denied':
      case 'claim_withdrawn':
        return this.claimResultData(<ClaimResultNotification>notification);
      case 'bounty_completed':
        return this.bountyCompletedData(<BountyCompletedNotification>(
          notification
        ));
      case 'refund':
          return this.refundData(notification);
    }
  }

  private refundData(notification: Notification) {
    return {
      ...this.baseNotificationData(notification),
      header: 'Your bounty pool contribution has been refunded',
      link: `/user/${this.user.id}`,
    };
  }

  private baseNotificationData(notification: Notification) {
    return {
      timeAgo: notification.timeAgo,
      icon: 'bell',
    };
  }

  private bountyCompletedData(notification: BountyCompletedNotification) {
    const success = !!notification.data.username;
    const event = success
      ? 'been marked as paid. You have earned a star!'
      : 'ended with no winner. Do you want to renew it?';

    return {
      ...this.baseNotificationData(notification),
      header: `Your bounty has ${event}`,
      link: `/bounties/${notification.data.bounty_id}`,
      icon: success ? 'money' : 'hourglass outline',
    };
  }

  private claimResultData(notification: ClaimResultNotification) {
    const action = notification.data.type.substr('claim_'.length);
    const success = notification.data.type === 'claim_accepted';
    const subject =
      notification.data.type === 'claim_withdrawn' ? 'their' : 'your';
    const targetSubject =
      notification.data.type === 'claim_withdrawn' ? 'your' : 'their';
    const after = success ? '. You should be paid within 7 days.' : '';

    return {
      ...this.baseNotificationData(notification),
      header: `${
        notification.data.username
      } has ${action} ${subject} claim for ${targetSubject} bounty reward${after}`,
      link: `/claims/${notification.data.claim_id}`,
      icon: success ? 'trophy' : 'times circle',
    };
  }

  private claimMadeData(notification: ClaimMadeNotification) {
    return {
      ...this.baseNotificationData(notification),
      header: `${
        notification.data.username
      } has made a claim for your bounty reward!`,
      link: `/claims/${notification.data.claim_id}`,
      icon: 'circle outline',
    };
  }

  private bountyCommentData(notification: BountyCommentNotification) {
    return {
      ...this.baseNotificationData(notification),
      header: `${notification.data.username} commented on your bounty${
        notification.data.type === 'bounty_comment' ? '' : ' claim'
      }`,
      link: `/bounties/${notification.data.bounty_id}`,
      icon: 'conversation',
    };
  }

  private claimCommentData(notification: ClaimCommentNotification) {
    return {
      ...this.baseNotificationData(notification),
      header: `${notification.data.username} commented on a bounty claim`,
      link: `/claims/${notification.data.claim_id}`,
      icon: 'conversation',
    };
  }
}
